import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, addDays } from "date-fns";
import {
  ArrowLeft, Plus, Minus, ShoppingCart, Trash2, ChevronRight,
  CalendarIcon, Loader2, Upload, X, Truck, Package
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuildBundlePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [step, setStep] = useState("select"); // "select" | "details"

  // Booking form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [duration, setDuration] = useState("1");
  const [address, setAddress] = useState("");
  const [deliveryOption, setDeliveryOption] = useState("");
  const [specialReqs, setSpecialReqs] = useState("");
  const [wireReqs, setWireReqs] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [idImage, setIdImage] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [contractAgreed, setContractAgreed] = useState(false);
  const [returningCustomer, setReturningCustomer] = useState(null);
  const [checkingCustomer, setCheckingCustomer] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/speakers`).then(res => {
      setSpeakers(res.data);
      // Pre-add item from URL param
      const addId = searchParams.get("add");
      if (addId) {
        const item = res.data.find(s => s.id === addId);
        if (item) {
          setCart({ [addId]: { ...item, qty: 1, days: 1, date: null, start_time: "", end_time: "" } });
          toast.success(`${item.name} added to your order`);
        }
      }
    }).catch(() => {
      toast.error("Failed to load equipment");
    }).finally(() => setLoading(false));
  }, []);

  const addToCart = (id) => {
    const speaker = speakers.find(s => s.id === id);
    if (!speaker) return;
    setCart(prev => {
      const current = prev[id]?.qty || 0;
      if (current >= speaker.quantity) {
        toast.error(`Only ${speaker.quantity} available`);
        return prev;
      }
      return { ...prev, [id]: { ...speaker, qty: current + 1, days: prev[id]?.days || 1, date: prev[id]?.date || null, start_time: prev[id]?.start_time || "", end_time: prev[id]?.end_time || "" } };
    });
  };

  const removeFromCart = (id) => {
    setCart(prev => {
      const current = prev[id]?.qty || 0;
      if (current <= 1) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: { ...prev[id], qty: current - 1 } };
    });
  };

  const updateItemDays = (id, days) => {
    const d = Math.max(0, Math.min(30, parseInt(days) || 0));
    setCart(prev => ({ ...prev, [id]: { ...prev[id], days: d } }));
  };

  const updateItemDate = (id, newDate) => {
    setCart(prev => ({ ...prev, [id]: { ...prev[id], date: newDate } }));
  };

  const updateItemTime = (id, field, value) => {
    setCart(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const clearItem = (id) => {
    setCart(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const cartItems = Object.values(cart);
  const cartCount = cartItems.reduce((sum, item) => sum + item.qty, 0);
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price_per_day * item.qty * Math.max(1, item.days || 1)), 0);

  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setIdImage(reader.result);
      setIdPreview(URL.createObjectURL(file));
    };
    reader.readAsDataURL(file);
  };

  const checkReturningCustomer = async (emailVal) => {
    if (!emailVal || !emailVal.includes("@")) return;
    setCheckingCustomer(true);
    try {
      const res = await axios.get(`${API}/customers/check`, { params: { email: emailVal } });
      if (res.data.returning && res.data.id_on_file) {
        setReturningCustomer(res.data);
        if (res.data.customer_name && !name) setName(res.data.customer_name);
        if (res.data.customer_phone && !phone) setPhone(res.data.customer_phone);
        toast.success("Welcome back! Your ID is already on file.");
      } else {
        setReturningCustomer(null);
      }
    } catch {
      setReturningCustomer(null);
    } finally {
      setCheckingCustomer(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!deliveryOption) {
      toast.error("Please select delivery or collection");
      return;
    }
    if (deliveryOption === "delivery" && !address) {
      toast.error("Please enter a delivery address");
      return;
    }
    // Check all items have dates
    const missingDate = cartItems.find(item => !item.date);
    if (missingDate) {
      toast.error(`Please select a date for ${missingDate.name}`);
      return;
    }
    if (!idImage && !returningCustomer?.id_on_file) {
      toast.error("Please upload a photo ID");
      return;
    }
    if (!contractAgreed) {
      toast.error("Please agree to the hire contract to continue");
      return;
    }

    setSubmitting(true);
    try {
      const items = cartItems.map(item => ({
        speaker_id: item.id,
        quantity: item.qty,
        days: item.days || 1
      }));

      const maxDays = Math.max(...cartItems.map(i => i.days || 1));
      // Use earliest item date as the event date
      const earliestDate = cartItems.reduce((earliest, item) => 
        !earliest || item.date < earliest ? item.date : earliest, null);

      // Build date details into special requirements
      const dateDetails = cartItems.map(item => {
        const timeStr = item.start_time && item.end_time ? ` (${item.start_time} - ${item.end_time})` : "";
        return `${item.name}: ${format(item.date, "dd/MM/yyyy")}${timeStr} for ${Math.max(1, item.days || 1)} day(s)`;
      }).join(', ');

      const res = await axios.post(`${API}/bookings/custom-bundle`, {
        items,
        customer_name: name,
        customer_email: email,
        customer_phone: phone,
        event_date: format(earliestDate, "yyyy-MM-dd"),
        event_duration_days: maxDays,
        delivery_address: deliveryOption === "delivery" ? address : "Collection",
        special_requirements: [`Delivery: ${deliveryOption === "delivery" ? "Setup & Delivery required" : "Customer collecting"}`, dateDetails, wireReqs ? `Wires: ${wireReqs}` : null, specialReqs].filter(Boolean).join(' | ') || null,
        discount_code: discountCode || null,
        id_image: idImage,
        contract_signed: contractAgreed,
        contract_name: name,
        contract_address: deliveryOption === "delivery" ? address : "Collection",
        contract_date: new Date().toLocaleDateString('en-GB')
      });

      navigate(`/booking-confirmation/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit booking");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF0033] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-white/70 hover:text-white transition-colors" data-testid="back-home">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src="https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/9204c88f36c8aba1418f399ed70957b9e9b435b9671cfdca88f8cc5e5e0a2467.png" alt="The Humble Hires" className="h-14" />
            <h1 className="text-lg font-bold uppercase tracking-wider">Build Your Order</h1>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <ShoppingCart className="w-5 h-5" />
            <span className="text-sm font-bold" data-testid="cart-count">{cartCount} items</span>
            {subtotal > 0 && (
              <span className="text-[#FF0033] font-bold ml-2" data-testid="cart-total">£{subtotal.toFixed(2)}</span>
            )}
          </div>
        </div>
      </header>

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {step === "select" && (
            <>
              <div className="mb-8">
                <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-2">Step 1</p>
                <h2 className="text-2xl md:text-4xl font-bold" data-testid="step-title">Choose Your Equipment</h2>
                <p className="text-white/50 mt-2">Pick the gear you need and set quantities. Set hire days per item in the cart below.</p>
              </div>

              {/* Equipment Grid */}
              <div className="mb-6">
                <h3 className="text-sm uppercase tracking-wider text-white/50 mb-4">Individual Equipment</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
                  {speakers.filter(s => !s.is_bundle).map(speaker => {
                    const inCart = cart[speaker.id]?.qty || 0;
                    return (
                      <div
                        key={speaker.id}
                        className={`neon-card overflow-hidden transition-all ${inCart > 0 ? "border-[#FF0033]/40" : ""}`}
                        data-testid={`equipment-${speaker.id}`}
                      >
                        <div className="relative h-40 overflow-hidden">
                          <img src={speaker.image_url} alt={speaker.name} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                          {inCart > 0 && (
                            <div className="absolute top-3 right-3 w-7 h-7 bg-[#FF0033] rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {inCart}
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <p className="text-xs text-[#FF0033] mono mb-1">{speaker.model}</p>
                          <h3 className="font-bold text-sm mb-1">{speaker.name}</h3>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-lg font-bold text-[#FF0033]">£{speaker.price_per_day}<span className="text-white/50 text-xs">/day</span></span>
                            <div className="flex items-center gap-1">
                              {inCart > 0 && (
                                <button onClick={() => removeFromCart(speaker.id)} className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded transition-colors" data-testid={`remove-${speaker.id}`}>
                                  <Minus className="w-4 h-4" />
                                </button>
                              )}
                              {inCart > 0 && <span className="w-8 text-center text-sm font-bold">{inCart}</span>}
                              <button onClick={() => addToCart(speaker.id)} disabled={!speaker.available || inCart >= speaker.quantity} className="w-8 h-8 flex items-center justify-center bg-[#FF0033] hover:bg-[#FF0033]/80 disabled:opacity-30 rounded transition-colors" data-testid={`add-${speaker.id}`}>
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bundles Grid */}
              <div className="mb-8">
                <h3 className="text-sm uppercase tracking-wider text-white/50 mb-4">Sound Bundles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                  {speakers.filter(s => s.is_bundle).map(bundle => {
                    const inCart = cart[bundle.id]?.qty || 0;
                    return (
                      <div
                        key={bundle.id}
                        className={`neon-card overflow-hidden transition-all border-[#FF0033]/20 ${inCart > 0 ? "border-[#FF0033]/50" : ""}`}
                        data-testid={`equipment-${bundle.id}`}
                      >
                        <div className="relative h-40 overflow-hidden">
                          <img src={bundle.image_url} alt={bundle.name} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                          <div className="absolute top-3 left-3 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#FF0033]/20 text-[#FF0033] rounded">Bundle</div>
                          {inCart > 0 && (
                            <div className="absolute top-3 right-3 w-7 h-7 bg-[#FF0033] rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {inCart}
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-bold text-sm mb-1">{bundle.name}</h3>
                          <p className="text-xs text-white/50 mb-2 line-clamp-1">{bundle.specs?.includes || bundle.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-lg font-bold text-[#FF0033]">£{bundle.price_per_day}<span className="text-white/50 text-xs">/day</span></span>
                            <div className="flex items-center gap-1">
                              {inCart > 0 && (
                                <button onClick={() => removeFromCart(bundle.id)} className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded transition-colors" data-testid={`remove-${bundle.id}`}>
                                  <Minus className="w-4 h-4" />
                                </button>
                              )}
                              {inCart > 0 && <span className="w-8 text-center text-sm font-bold">{inCart}</span>}
                              <button onClick={() => addToCart(bundle.id)} disabled={!bundle.available || inCart >= (bundle.quantity || 1)} className="w-8 h-8 flex items-center justify-center bg-[#FF0033] hover:bg-[#FF0033]/80 disabled:opacity-30 rounded transition-colors" data-testid={`add-${bundle.id}`}>
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Cart Summary */}
              {cartCount > 0 && (
                <div className="glass-panel border-[#FF0033]/20 p-6 mb-6" data-testid="cart-summary">
                  <h3 className="text-sm uppercase tracking-wider text-white/50 mb-4">Your Order</h3>
                  <div className="space-y-4 mb-6">
                    {cartItems.map(item => (
                      <div key={item.id} className="bg-white/5 border border-white/10 rounded-lg p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <img src={item.image_url} alt={item.name} className="w-10 h-10 object-cover rounded flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{item.qty}x {item.name}</p>
                              <p className="text-xs text-white/50">£{item.price_per_day}/day</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm font-bold">£{(item.price_per_day * item.qty * Math.max(1, item.days || 1)).toFixed(2)}</span>
                            <button onClick={() => clearItem(item.id)} className="text-white/40 hover:text-[#FF0055]" data-testid={`clear-${item.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white/50">Days:</span>
                            <div className="flex items-center gap-1 bg-black/40 border border-white/10 rounded px-2 py-1">
                              <button onClick={() => updateItemDays(item.id, (item.days || 1) - 1)} className="text-white/50 hover:text-white" disabled={(item.days || 0) <= 0}>
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="text-xs font-bold w-8 text-center">{item.days || 0}</span>
                              <button onClick={() => updateItemDays(item.id, (item.days || 0) + 1)} className="text-white/50 hover:text-white">
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white/50">Date:</span>
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="flex items-center gap-2 bg-black/40 border border-white/10 rounded px-3 py-1 text-xs hover:border-[#FF0033]/40 transition-colors">
                                  <CalendarIcon className="w-3 h-3 text-[#FF0033]" />
                                  {item.date ? format(item.date, "dd MMM yyyy") : "Select date"}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-white/10" align="start">
                                <Calendar
                                  mode="single"
                                  selected={item.date}
                                  onSelect={(d) => updateItemDate(item.id, d)}
                                  disabled={(d) => d < new Date()}
                                  className="rounded-md"
                                />
                              </PopoverContent>
                            </Popover>
                            {item.date && (item.days || 0) > 1 && (
                              <span className="text-xs text-white/40">to {format(addDays(item.date, (item.days || 1) - 1), "dd MMM yyyy")}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white/50">From:</span>
                            <input type="time" value={item.start_time || ""} onChange={e => updateItemTime(item.id, "start_time", e.target.value)} className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white h-7" data-testid={`start-time-${item.id}`} />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white/50">To:</span>
                            <input type="time" value={item.end_time || ""} onChange={e => updateItemTime(item.id, "end_time", e.target.value)} className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white h-7" data-testid={`end-time-${item.id}`} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-white/10 pt-4 flex items-center justify-between">
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider">Estimated Total</p>
                      <p className="text-3xl font-black text-[#FF0033]" data-testid="bundle-total">£{subtotal.toFixed(2)}</p>
                      <p className="text-white/40 text-xs mt-1">Delivery fee calculated on confirmation</p>
                    </div>
                    <Button
                      onClick={() => {
                        const missingDate = cartItems.find(item => !item.date);
                        if (missingDate) {
                          toast.error(`Please select a date for ${missingDate.name}`);
                          return;
                        }
                        setStep("details");
                      }}
                      className="bg-[#FF0033] text-white hover:bg-[#FF0033]/80 px-8 py-6 text-base font-bold"
                      data-testid="proceed-to-details"
                    >
                      Continue <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {cartCount === 0 && (
                <div className="text-center py-12 text-white/40">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-4" />
                  <p>Add equipment above to build your custom bundle</p>
                </div>
              )}
            </>
          )}

          {step === "details" && (
            <>
              <div className="mb-8">
                <button onClick={() => setStep("select")} className="flex items-center gap-2 text-white/50 hover:text-white mb-4 transition-colors" data-testid="back-to-select">
                  <ArrowLeft className="w-4 h-4" /> Back to equipment
                </button>
                <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-2">Step 2</p>
                <h2 className="text-2xl md:text-4xl font-bold" data-testid="step-details-title">Your Details</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Booking Form */}
                <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
                  <div className="glass-panel p-6 space-y-4">
                    <h3 className="text-sm uppercase tracking-wider text-white/50 mb-2">Personal Info</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Full Name *</label>
                        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="bg-black/60 border-white/10 h-12" data-testid="form-name" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Email *</label>
                        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="bg-black/60 border-white/10 h-12" data-testid="form-email" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Phone *</label>
                        <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="07xxx xxxxxx" className="bg-black/60 border-white/10 h-12" data-testid="form-phone" />
                      </div>
                    </div>
                  </div>

                  <div className="glass-panel p-6 space-y-4">
                    <h3 className="text-sm uppercase tracking-wider text-white/50 mb-2">Event Details</h3>
                    
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Setup & Delivery *</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setDeliveryOption("delivery")}
                          className={`p-4 rounded-lg border text-center transition-all ${deliveryOption === "delivery" ? "border-[#FF0033] bg-[#FF0033]/10 text-white" : "border-white/10 bg-black/40 text-white/50 hover:border-white/20"}`}
                          data-testid="option-delivery"
                        >
                          <Truck className="w-6 h-6 mx-auto mb-2" />
                          <p className="text-sm font-bold">Delivery & Setup</p>
                          <p className="text-xs text-white/50 mt-1">We deliver and set up for you</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeliveryOption("collection")}
                          className={`p-4 rounded-lg border text-center transition-all ${deliveryOption === "collection" ? "border-[#FF0033] bg-[#FF0033]/10 text-white" : "border-white/10 bg-black/40 text-white/50 hover:border-white/20"}`}
                          data-testid="option-collection"
                        >
                          <Package className="w-6 h-6 mx-auto mb-2" />
                          <p className="text-sm font-bold">Collection</p>
                          <p className="text-xs text-white/50 mt-1">I'll collect the equipment</p>
                        </button>
                      </div>
                    </div>

                    {deliveryOption === "delivery" && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Delivery Address *</label>
                        <Textarea value={address} onChange={e => setAddress(e.target.value)} placeholder="Full delivery address" rows={2} className="bg-black/60 border-white/10 resize-none" data-testid="form-address" />
                      </div>
                    )}
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Wire / Cable Requirements</label>
                      <Textarea value={wireReqs} onChange={e => setWireReqs(e.target.value)} placeholder="e.g. XLR cables, extension leads, aux cable, RCA to jack..." rows={2} className="bg-black/60 border-white/10 resize-none" data-testid="form-wires" />
                      <p className="text-xs text-white/50 mt-1">Not sure? <a href="https://wa.me/447543710680" target="_blank" rel="noopener noreferrer" className="text-[#FF0033] hover:underline">Message us on WhatsApp</a></p>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Special Requirements</label>
                      <Textarea value={specialReqs} onChange={e => setSpecialReqs(e.target.value)} placeholder="Any other special instructions..." rows={2} className="bg-black/60 border-white/10 resize-none" data-testid="form-reqs" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Discount Code</label>
                      <Input value={discountCode} onChange={e => setDiscountCode(e.target.value.toUpperCase())} placeholder="Enter code" className="bg-black/60 border-white/10 h-12 uppercase" data-testid="form-discount" />
                    </div>
                  </div>

                  <div className="glass-panel p-6">
                    <h3 className="text-sm uppercase tracking-wider text-white/50 mb-4">Photo ID *</h3>
                    <input type="file" ref={fileRef} onChange={handleIdUpload} accept="image/*" className="hidden" />
                    {idPreview ? (
                      <div className="relative">
                        <img src={idPreview} alt="ID" className="max-h-48 rounded border border-white/10" />
                        <button onClick={() => { setIdImage(null); setIdPreview(null); }} className="absolute top-2 right-2 w-6 h-6 bg-black/80 rounded-full flex items-center justify-center" data-testid="remove-id">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="w-full border-2 border-dashed border-white/10 hover:border-[#FF0033]/40 rounded-lg p-8 flex flex-col items-center gap-2 text-white/50 hover:text-white/70 transition-colors"
                        data-testid="upload-id"
                      >
                        <Upload className="w-8 h-8" />
                        <span className="text-sm">Upload photo ID (passport or driving licence)</span>
                      </button>
                    )}
                  </div>

                  {/* Hire Contract */}
                  <div className="glass-panel p-6" data-testid="hire-contract">
                    <h3 className="text-sm uppercase tracking-wider text-[#FF0033] mb-4 font-bold">Hire Agreement</h3>
                    <div className="bg-black/40 border border-white/10 rounded-lg p-4 mb-4 max-h-60 overflow-y-auto text-xs text-white/70 space-y-2">
                      <p className="font-bold text-white text-sm">The Humble Hires — Equipment Hire Contract</p>
                      <p>By submitting this booking request, I agree to the following terms:</p>
                      <p><strong className="text-white">1. Care of Equipment:</strong> I accept full responsibility for all hired equipment from the point of delivery/collection until its return. Any damage, loss or theft will be charged at full replacement cost.</p>
                      <p><strong className="text-white">2. Payment:</strong> A deposit or full payment is required to secure the booking. The remaining balance (if applicable) must be paid no later than 48 hours before the event date.</p>
                      <p><strong className="text-white">3. Cancellations:</strong> Cancellations made less than 48 hours before the event will forfeit the deposit. Cancellations made more than 48 hours in advance will receive a full refund.</p>
                      <p><strong className="text-white">4. Late Returns:</strong> Equipment returned late will be charged at the standard daily rate for each additional day.</p>
                      <p><strong className="text-white">5. Setup & Usage:</strong> Equipment must be used in a safe and appropriate manner. The Humble Hires is not liable for any injuries or damages caused by misuse of equipment.</p>
                      <p><strong className="text-white">6. Identification:</strong> A valid form of photo ID is required for all bookings and will be held on file.</p>
                      <p><strong className="text-white">7. Delivery & Collection:</strong> Setup and collection times will be agreed prior to the event. The hirer must ensure access is available at the agreed times.</p>
                    </div>
                    <div className="glass-panel p-4 mb-4 space-y-3">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Print Name *</label>
                        <Input value={name} readOnly className="bg-black/60 border-white/10 h-10 text-white font-bold" data-testid="contract-print-name" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Address *</label>
                        <Input value={address} readOnly className="bg-black/60 border-white/10 h-10 text-white" data-testid="contract-address" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Date</label>
                        <Input value={new Date().toLocaleDateString('en-GB')} readOnly className="bg-black/60 border-white/10 h-10 text-white/70" />
                      </div>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="contract-agree-label">
                      <input
                        type="checkbox"
                        checked={contractAgreed}
                        onChange={e => setContractAgreed(e.target.checked)}
                        className="mt-1 w-5 h-5 accent-[#FF0033] rounded"
                        data-testid="contract-agree-checkbox"
                      />
                      <span className="text-sm text-white/70">I, <strong className="text-white">{name || '___'}</strong>, confirm I have read and agree to the hire contract terms above.</span>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting || !contractAgreed}
                    className="w-full bg-[#FF0033] text-white hover:bg-[#FF0033]/80 py-6 text-lg font-bold disabled:opacity-40"
                    data-testid="submit-bundle-booking"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Booking Request"}
                  </Button>
                </form>

                {/* Order Summary Sidebar */}
                <div className="lg:col-span-1">
                  <div className="glass-panel p-6 sticky top-28 border-[#FF0033]/20" data-testid="order-summary">
                    <h3 className="text-sm uppercase tracking-wider text-white/50 mb-4">Order Summary</h3>
                    <div className="space-y-3 mb-6">
                      {cartItems.map(item => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span className="text-white/70">{item.qty}x {item.name}</span>
                          <span className="font-bold">£{(item.price_per_day * item.qty * (item.days || 1)).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-white/10 pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Duration</span>
                        <span>Per item</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Delivery</span>
                        <span className="text-white/50">TBC</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/10">
                        <span>Total</span>
                        <span className="text-[#FF0033]">£{subtotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
